import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import AdminClinicsListTemplate from '@/components/templates/AdminClinicsListTemplate';
import ClinicUsersFilterPanel from './components/ClinicUsersFilterPanel';
import ClinicUsersTableSection from './components/ClinicUsersTableSection';
import useAppToast from '@/hooks/useAppToast';
import { useClinicUserListQuery } from '@/hooks/admin/useClinicUserListQuery';
import { downloadClinicUsersCsv } from '@/services/admin/clinicUsers';
import type { ClinicUserListParams, ClinicUserListItem } from '@/services/admin/clinicUsers';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';

const DEFAULT_PER_PAGE = 20;

/** Matches `useClinicUserListQuery` queryKey prefix for refetch on 検索. */
const clinicUserListQueryPrefix = (site: string) => ['admin', 'clinic-users', 'list', site] as const;

const COLUMN_TO_API_FIELD: Record<string, string> = {
  name: 'clinic_users.name',
  clinic_name: 'clinic_informations.name',
};

const ClinicUserListPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { isLoading: isAuthLoading } = useSiteAdminAuth();
  const toast = useAppToast();

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');

  // --- Tenants (api/v1/tenants/site-types) ---
  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : site ?? ''],
    queryFn: () =>
      getTenantSiteTypes(isSuperAdminRoute ? undefined : site ?? undefined),
    enabled: !isAuthLoading && !!site,
    staleTime: Infinity,
  });

  const tenantSiteTypeList: TenantSiteTypeItem[] =
    siteTypesQuery.data?.data?.site_types ?? [];

  const canUseSiteTypeFeatures = tenantSiteTypeList.length > 1;

  const tenants = tenantSiteTypeList;
  const tenantNameBySiteType = useMemo(() => {
    const map = new Map<string, string>();
    tenantSiteTypeList.forEach(t => {
      if (t.site_type) map.set(t.site_type, t.name);
    });
    return map;
  }, [tenantSiteTypeList]);

  useEffect(() => {
    if (siteTypesQuery.isError) {
      toast.error('サイト一覧の取得に失敗しました。');
    }
  }, [siteTypesQuery.isError, toast]);

  // --- Filter state (input values) ---
  const [searchLoginId, setSearchLoginId] = useState('');
  const [searchName, setSearchName] = useState('');
  const [siteTypes, setSiteTypes] = useState<string[]>([]);
  // --- Applied filters (sent to API when user clicks 検索) ---
  const [appliedSearchLoginId, setAppliedSearchLoginId] = useState('');
  const [appliedSearchName, setAppliedSearchName] = useState('');
  const [appliedSiteTypes, setAppliedSiteTypes] = useState<string[]>([]);

  const tenantSiteTypes = useMemo(
    () => tenants.map(t => t.site_type).filter(Boolean) as string[],
    [tenants]
  );
  const hasAllSelected =
    tenantSiteTypes.length > 0 && tenantSiteTypes.every(st => siteTypes.includes(st));

  // --- Sort / page ---
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);

  const handleSort = useCallback(
    (key: string | null, order: 'ascend' | 'descend' | null) => {
      setSortKey(key);
      setSortOrder(order);
      setPage(1);
    },
    []
  );

  const apiSort = useMemo<[string, 'asc' | 'desc'] | undefined>(() => {
    if (!sortKey || !sortOrder) return undefined;
    const field = COLUMN_TO_API_FIELD[sortKey];
    if (!field) return undefined;
    return [field, sortOrder === 'ascend' ? 'asc' : 'desc'];
  }, [sortKey, sortOrder]);

  const params: ClinicUserListParams = useMemo(
    () => ({
      search_login_id: appliedSearchLoginId.trim() || undefined,
      search_name: appliedSearchName.trim() || undefined,
      site_types:
        canUseSiteTypeFeatures && appliedSiteTypes.length > 0 ? appliedSiteTypes : undefined,
      sort: apiSort,
      page,
      per_page: DEFAULT_PER_PAGE,
    }),
    [appliedSearchLoginId, appliedSearchName, appliedSiteTypes, canUseSiteTypeFeatures, apiSort, page]
  );

  const { data, isLoading, meta } = useClinicUserListQuery(site || '', params);
  const rows: ClinicUserListItem[] = data?.data || [];

  const handleSearch = useCallback(() => {
    if (!site) return;
    flushSync(() => {
      setPage(1);
      setAppliedSearchLoginId(searchLoginId);
      setAppliedSearchName(searchName);
      setAppliedSiteTypes(siteTypes);
    });
    void queryClient.refetchQueries({
      queryKey: clinicUserListQueryPrefix(site),
      type: 'active',
    });
  }, [searchLoginId, searchName, siteTypes, site, queryClient]);

  const [csvLoading, setCsvLoading] = useState(false);
  const handleCsv = useCallback(async () => {
    if (!site) return;
    setCsvLoading(true);
    try {
      await downloadClinicUsersCsv(site, params);
    } catch {
      toast.error('CSVのダウンロードに失敗しました。');
    } finally {
      setCsvLoading(false);
    }
  }, [site, params, toast]);

  if (isAuthLoading) return null;

  return (
    <AdminClinicsListTemplate
      breadcrumbItems={[{ title: '治療院ユーザーCSV' }]}
      title="治療院ユーザーCSV"
      filter={
        <ClinicUsersFilterPanel
          canUseSiteTypeFeatures={canUseSiteTypeFeatures}
          tenants={tenants}
          siteTypes={siteTypes}
          hasAllSelected={hasAllSelected}
          onChangeSiteTypes={setSiteTypes}
          searchLoginId={searchLoginId}
          onChangeSearchLoginId={setSearchLoginId}
          searchName={searchName}
          onChangeSearchName={setSearchName}
          onSearch={handleSearch}
          onCsv={handleCsv}
          csvLoading={csvLoading}
        />
      }
      table={
        <ClinicUsersTableSection
          rows={rows}
          loading={isLoading}
          meta={meta ?? { current_page: 1, last_page: 1, per_page: DEFAULT_PER_PAGE, total: 0 }}
          defaultPerPage={DEFAULT_PER_PAGE}
          canUseSiteTypeFeatures={canUseSiteTypeFeatures}
          tenantNameBySiteType={tenantNameBySiteType}
          sortKey={sortKey}
          sortOrder={sortOrder}
          onSort={handleSort}
          onChangePage={setPage}
        />
      }
    />
  );
};

export default ClinicUserListPage;
